import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const PlusComparisonModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation('shared');
  if (!isOpen) return null;

  const comparisonData = [
    t('plusComparisonModal.rows.chat', { returnObjects: true }),
    t('plusComparisonModal.rows.calendar', { returnObjects: true }),
    t('plusComparisonModal.rows.sounds', { returnObjects: true }),
    t('plusComparisonModal.rows.themes', { returnObjects: true }),
    t('plusComparisonModal.rows.fileSharing', { returnObjects: true }),
  ];

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100001]"
          />

          {/* Modal - Viewport Center */}
          <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[100002] w-full max-w-4xl p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, y: -30, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -30, scale: 0.95 }}
              transition={{
                type: 'spring',
                stiffness: 300,
                damping: 30,
              }}
              className="relative w-full bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-3xl shadow-2xl border border-amber-500/30 overflow-hidden pointer-events-auto max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Decorative Gold Glow */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-500/10 to-transparent opacity-50" />
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent" />

              {/* Close Button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 z-10 p-2 rounded-xl bg-gray-800/80 hover:bg-gray-700/80 text-[var(--color-text-muted)] hover:text-white transition-all duration-200 border border-gray-700/50 hover:border-amber-500/50"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Content */}
              <div className="relative p-8 md:p-12">
                {/* Header */}
                <div className="text-center mb-8">
                  <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="text-3xl font-bold bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 bg-clip-text text-transparent mb-2"
                  >
                    {t('plusComparisonModal.title')}
                  </motion.h2>
                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="text-[var(--color-text-muted)] text-sm"
                  >
                    {t('plusComparisonModal.subtitle')}
                  </motion.p>
                </div>

                {/* Comparison Table */}
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-gray-700/50">
                        <th className="text-left py-4 px-4 text-[var(--color-text-muted)] font-semibold text-sm">{t('plusComparisonModal.featureHeader')}</th>
                        <th className="text-center py-4 px-4 text-[var(--color-text-muted)] font-semibold text-sm w-1/3">{t('plusComparisonModal.freeHeader')}</th>
                        <th className="text-center py-4 px-4 text-amber-400 font-semibold text-sm w-1/3">{t('plusComparisonModal.plusHeader')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparisonData.map((item, index) => (
                        <motion.tr
                          key={item.feature}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.3 + index * 0.1 }}
                          className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors"
                        >
                          <td className="py-4 px-4 text-white font-medium">{item.feature}</td>
                          <td className="py-4 px-4 text-[var(--color-text-muted)] text-sm text-center">
                            <div className="flex items-center justify-center space-x-2">
                              <XCircle className="w-4 h-4 text-[var(--color-text-secondary)] flex-shrink-0" />
                              <span>{item.free}</span>
                            </div>
                          </td>
                          <td className="py-4 px-4 text-amber-300 text-sm text-center">
                            <div className="flex items-center justify-center space-x-2">
                              <Check className="w-5 h-5 text-amber-400 flex-shrink-0" strokeWidth={2.5} />
                              <span>{item.plus}</span>
                            </div>
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );

  // Render modal to document.body using Portal
  if (typeof document !== 'undefined') {
    return createPortal(modalContent, document.body);
  }

  return modalContent;
};

export default PlusComparisonModal;
